import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import AdminNav from "../components/AdminNav";
import { api, TOOLS, TOOL_LABEL, TOOL_COLOR } from "../lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCw, Star } from "lucide-react";

const COLORS = TOOL_COLOR;

const StatBlock = ({ label, value, color }) => (
  <div
    data-testid={`stat-${label}`}
    className="border-4 border-[#1a1a1a] rounded-3xl p-5 shadow-hard-sm bg-white"
  >
    <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2">
      <span className="w-3 h-3 rounded-full border-2 border-[#1a1a1a]" style={{ background: color }} />
      {label}
    </div>
    <div className="font-display font-black text-4xl sm:text-5xl tabular-nums">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [tool, setTool] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/admin/stats?days=${days}`),
        api.get(`/admin/prompts?page=${page}&page_size=25&days=${days}${tool ? `&tool=${tool}` : ""}`),
      ]);
      setStats(s.data);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("guango_admin_token");
        navigate("/admin");
      } else {
        toast.error("No se pudo cargar el panel.");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("guango_admin_token");
    localStorage.removeItem("guango_admin_email");
    navigate("/admin");
  };

  const toggleFeatured = async (id) => {
    try {
      const res = await api.post(`/admin/prompts/${id}/feature`);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, featured: res.data.featured } : r)));
    } catch {
      toast.error("No se pudo destacar.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, tool, days]);

  const maxByDay = stats?.by_day?.reduce((m, d) => Math.max(m, d.count), 0) || 1;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-24">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-3">
              admin · panel
            </span>
            <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight">historial.</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={3} /> recargar
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="press inline-flex items-center gap-2 bg-[#ef4444] text-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
            >
              <LogOut className="w-4 h-4" strokeWidth={3} /> salir
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatBlock label="total" value={stats?.total ?? 0} color="#1a1a1a" />
          {TOOLS.map((t) => (
            <StatBlock
              key={t.id}
              label={t.id}
              value={stats?.by_tool?.[t.id] ?? 0}
              color={COLORS[t.id]}
            />
          ))}
        </div>

        {/* Daily chart (simple bars) */}
        <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-6 sm:p-8 mb-8" data-testid="daily-chart">
          <div className="flex flex-wrap items-end justify-between mb-5 gap-2">
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">por día</h2>
            <div className="flex gap-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`days-${d}`}
                  onClick={() => { setDays(d); setPage(1); }}
                  className={`press border-4 border-[#1a1a1a] rounded-lg px-3 py-1 font-mono text-xs uppercase font-bold shadow-hard-sm ${
                    days === d ? "bg-[#22c55e]" : "bg-white"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-1 h-44 overflow-x-auto pb-1">
            {(stats?.by_day || []).length === 0 && (
              <div className="font-mono text-sm text-[#737373]">Sin datos todavía. Genera algunos prompts.</div>
            )}
            {(stats?.by_day || []).map((d) => (
              <div key={d.date} className="flex flex-col items-center gap-1 min-w-[26px]">
                <div
                  className="w-5 sm:w-6 border-2 border-[#1a1a1a] rounded-t"
                  style={{
                    height: `${(d.count / maxByDay) * 150}px`,
                    background: "#22c55e",
                  }}
                  title={`${d.date}: ${d.count}`}
                />
                <div className="font-mono text-[9px] text-[#4a4a4a] -rotate-45 origin-top-left whitespace-nowrap mt-2">
                  {d.date.slice(5)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold mr-1">filtrar:</span>
          <button
            data-testid="filter-all"
            onClick={() => { setTool(""); setPage(1); }}
            className={`press border-4 border-[#1a1a1a] rounded-full px-4 py-1.5 font-mono text-xs uppercase font-bold shadow-hard-sm ${
              tool === "" ? "bg-[#1a1a1a] text-white" : "bg-white"
            }`}
          >
            todas
          </button>
          {TOOLS.map((t) => (
            <button
              key={t.id}
              data-testid={`filter-${t.id}`}
              onClick={() => { setTool(t.id); setPage(1); }}
              className={`press border-4 border-[#1a1a1a] rounded-full px-4 py-1.5 font-mono text-xs uppercase font-bold shadow-hard-sm`}
              style={{ background: tool === t.id ? COLORS[t.id] : "#fff" }}
            >
              {t.title}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md overflow-hidden" data-testid="prompts-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-3 py-3 w-10">★</th>
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">fecha</th>
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">herramienta</th>
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">prompt</th>
                  <th className="text-right font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">vistas</th>
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">país</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="font-mono text-sm p-8 text-center text-[#737373]">
                      Sin registros para este filtro.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t-2 border-[#1a1a1a]/15 align-top" data-testid={`row-${r.id}`}>
                    <td className="px-3 py-3">
                      <button
                        data-testid={`feature-${r.id}`}
                        onClick={() => toggleFeatured(r.id)}
                        title={r.featured ? "Quitar de destacados" : "Marcar como destacado"}
                        className="w-7 h-7 rounded-md border-2 border-[#1a1a1a] flex items-center justify-center"
                        style={{ background: r.featured ? "#FFB000" : "#fff" }}
                      >
                        <Star className={`w-4 h-4 ${r.featured ? "fill-current" : ""}`} strokeWidth={3} />
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap text-[#4a4a4a]">
                      {new Date(r.created_at).toLocaleString("es")}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-block font-mono text-[10px] uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-2 py-0.5"
                        style={{ background: COLORS[r.tool] || "#fff" }}
                      >
                        {TOOL_LABEL[r.tool] || r.tool}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm max-w-xl">
                      <a href={`/p/${r.id}`} target="_blank" rel="noreferrer" className="hover:underline decoration-[#22c55e] decoration-2">
                        {r.prompt}
                      </a>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs tabular-nums text-right text-[#4a4a4a]">{r.views || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#4a4a4a]">{r.country || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="border-t-4 border-[#1a1a1a] flex items-center justify-between px-4 py-3 bg-[#f7f5f0]">
            <div className="font-mono text-xs uppercase tracking-wider">
              página {page} / {totalPages} · {total} registros
            </div>
            <div className="flex gap-2">
              <button
                data-testid="prev-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="press border-4 border-[#1a1a1a] rounded-lg px-3 py-1.5 font-mono text-xs uppercase font-bold shadow-hard-sm bg-white disabled:opacity-40"
              >
                ← prev
              </button>
              <button
                data-testid="next-page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="press border-4 border-[#1a1a1a] rounded-lg px-3 py-1.5 font-mono text-xs uppercase font-bold shadow-hard-sm bg-white disabled:opacity-40"
              >
                next →
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdminDashboard;
